import React, {useEffect, useState, useMemo} from 'react'
import {AUTO_FIXABLE_TYPES, A11Y_SUGGESTIONS, WarningType} from '../../utils/processHtmlForA11y'
import {getA11yAiProvider, ProviderCapabilities} from '../../utils/ai/aiSuggest'

type Issue = {type: string; message: string; context?: string; label?: string; helpUrl?: string; impact?: string}

const LABEL: Record<string, string> = {
  'heading-skip': 'Heading level skip',
  'empty-link': 'Empty link',
  'generic-link-text': 'Generic link text',
  'empty-alt': 'Image missing alt text',
  'image-link-no-alt': 'Image link — no alt text',
  'dead-link': 'Dead link (goes nowhere)',
  'new-tab-no-warning': 'New tab — no user warning',
  'duplicate-link-text': 'Duplicate link text',
  'table-no-headers': 'Table — no header cells',
  'table-empty-header': 'Table — empty header cell',
  'table-no-caption': 'Table — no caption',
  'table-merged-no-headers': 'Table — merged cell missing headers',
  'table-nested': 'Nested table',
  'table-auto-fixed': 'Table structure auto-corrected',
}

// Maps each warning type to the provider capability that can fix it.
// alt + link are live on-device; table header/caption are wired but
// capability-gated OFF on-device — their buttons appear only when a
// Phase 2 provider reports the capability true. No UI change needed then.
const AI_SUGGESTABLE: Record<string, keyof ProviderCapabilities> = {
  'empty-alt': 'altText',
  'image-link-no-alt': 'altText',
  'generic-link-text': 'linkText',
  'empty-link': 'linkText',
  'table-no-headers': 'headerLabel',
  'table-empty-header': 'headerLabel',
  'table-no-caption': 'tableCaption',
}

const IssueCard: React.FC<{issue: Issue; color: 'red' | 'yellow'; onClick?: () => void; suggestion?: string; onSuggest?: () => void; loading?: boolean}> = ({issue, color, onClick, suggestion, onSuggest, loading}) => {
  const isRed = color === 'red'
  return (
    <div
      onClick={onClick}
      style={{
        border: `1px solid ${isRed ? '#fca5a5' : '#fcd34d'}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        background: isRed ? '#fff5f5' : '#fffbeb',
        cursor: onClick ? 'pointer' : 'default',
      }}
      title={onClick ? 'Click to highlight in editor' : undefined}
    >
      <div style={{fontWeight: 600, fontSize: 13, color: isRed ? '#b91c1c' : '#b45309', marginBottom: 4}}>
        {issue.label ?? LABEL[issue.type] ?? issue.type}
      </div>
      <div style={{fontSize: 13, color: '#374151', marginBottom: issue.context || suggestion ? 6 : 0}}>
        {issue.message}
      </div>
      {issue.context && (
        <code
          style={{
            display: 'block',
            fontSize: 11,
            background: 'rgba(0,0,0,0.06)',
            padding: '4px 6px',
            borderRadius: 4,
            color: '#4b5563',
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
          }}
        >
          {issue.context}
        </code>
      )}
      {issue.helpUrl && (
        <a
          href={issue.helpUrl}
          target='_blank'
          rel='noopener noreferrer'
          onClick={(e) => e.stopPropagation()}
          style={{display: 'inline-block', marginTop: 6, fontSize: 11, color: '#2563eb'}}
        >
          Learn more ↗
        </a>
      )}
      {suggestion && (
        <div style={{fontSize: 11, color: '#6b7280', marginTop: 6, borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 4}}>
          💡 {suggestion}
        </div>
      )}
      {onSuggest && (
        <button
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation()
            if (!loading) onSuggest()
          }}
          style={{
            marginTop: 8,
            padding: '4px 10px',
            border: 'none',
            borderRadius: 6,
            background: '#7c3aed',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? 'default' : 'pointer',
            opacity: loading ? 0.85 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {loading ? (
            <>
              <span
                style={{
                  width: 12,
                  height: 12,
                  border: '2px solid rgba(255,255,255,0.5)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'a11ySpin 0.7s linear infinite',
                }}
              />
              Suggesting…
            </>
          ) : (
            '✨ Suggest with AI'
          )}
        </button>
      )}
    </div>
  )
}

const A11yWarningPanel: React.FC = () => {
  const [violations, setViolations] = useState<Issue[]>([])
  const [warnings, setWarnings] = useState<Issue[]>([])
  const [open, setOpen] = useState(false)
  const [scanResults, setScanResults] = useState<Issue[]>([])
  const [scanned, setScanned] = useState(false)
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  const aiProvider = useMemo(() => getA11yAiProvider(), [])
  const canSuggest = (type: string): boolean => {
    const cap = AI_SUGGESTABLE[type]
    return !!cap && aiProvider.capabilities[cap]
  }
  const issueKey = (i: Issue): string => `${i.type}::${i.context ?? ''}`
  const requestSuggestion = (issue: Issue) => {
    setPendingKey(issueKey(issue)) // show the spinner on this card's button
    window.dispatchEvent(new CustomEvent('a11y/aiSuggest', {detail: {type: issue.type, context: issue.context}}))
  }

  useEffect(() => {
    const onBlocked = (e: Event) => {
      setViolations(((e as CustomEvent).detail as Issue[]) ?? [])
      setOpen(true)
    }
    const onWarned = (e: Event) => {
      setWarnings(((e as CustomEvent).detail as Issue[]) ?? [])
    }
    // Live re-validation after a fix (auto or AI) or while editing: refresh the
    // lists/badge in place WITHOUT forcing the drawer open.
    const onRevalidate = (e: Event) => {
      const detail = (e as CustomEvent).detail as {violations?: Issue[]; warnings?: Issue[]}
      setViolations(detail?.violations ?? [])
      setWarnings(detail?.warnings ?? [])
    }
    const onScan = (e: Event) => {
      setScanResults(((e as CustomEvent).detail as Issue[]) ?? [])
      setScanned(true)
      setOpen(true)
    }
    const onEditorClosed = () => {
      setViolations([])
      setWarnings([])
      setScanResults([])
      setScanned(false)
      setPendingKey(null)
      setOpen(false)
    }
    // Clear the spinner once the editor finishes the suggestion (success or failure).
    const onSuggestDone = () => setPendingKey(null)
    const onForceSaveDone = () => {
      setViolations([])
      setWarnings([])
      setOpen(false)
    }
    window.addEventListener('a11y/violationsBlocked', onBlocked)
    window.addEventListener('a11y/warningsDispatched', onWarned)
    window.addEventListener('a11y/revalidate', onRevalidate)
    window.addEventListener('a11y/scanResults', onScan)
    window.addEventListener('a11y/aiSuggestDone', onSuggestDone)
    window.addEventListener('a11y/editorClosed', onEditorClosed)
    window.addEventListener('a11y/forceSaveDone', onForceSaveDone)
    return () => {
      window.removeEventListener('a11y/violationsBlocked', onBlocked)
      window.removeEventListener('a11y/warningsDispatched', onWarned)
      window.removeEventListener('a11y/revalidate', onRevalidate)
      window.removeEventListener('a11y/scanResults', onScan)
      window.removeEventListener('a11y/aiSuggestDone', onSuggestDone)
      window.removeEventListener('a11y/editorClosed', onEditorClosed)
      window.removeEventListener('a11y/forceSaveDone', onForceSaveDone)
    }
  }, [])

  const total = violations.length + warnings.length + scanResults.length
  if (total === 0 && !scanned) return null

  const hasBlocking = violations.length > 0
  const hasAutoFixable = warnings.some(w => AUTO_FIXABLE_TYPES.has(w.type as WarningType))
  const accentColor = hasBlocking ? '#dc3545' : '#f59e0b'
  const accentDark = hasBlocking ? '#991b1b' : '#92400e'

  const handleFixAll = () => {
    setWarnings(prev => prev.filter(w => !AUTO_FIXABLE_TYPES.has(w.type as WarningType)))
    window.dispatchEvent(new CustomEvent('a11y/applyFixes'))
  }

  return (
    <>
      <style>{'@keyframes a11ySpin { to { transform: rotate(360deg); } }'}</style>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 99999,
            border: 'none',
            borderRadius: 8,
            padding: '10px 16px',
            background: accentColor,
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              background: '#fff',
              color: accentDark,
              borderRadius: '50%',
              width: 22,
              height: 22,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            {total}
          </span>
          {hasBlocking ? 'Save blocked — accessibility issues' : 'Accessibility warnings'}
        </button>
      )}

      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 380,
          background: '#fff',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
          zIndex: 99999,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            background: accentColor,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div>
            <div style={{fontWeight: 700, fontSize: 16}}>Accessibility Issues</div>
            <div style={{fontSize: 12, opacity: 0.9}}>
              {hasBlocking ? 'Content was NOT saved — fix these first' : 'Content saved with warnings'}
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            style={{
              background: 'rgba(255,255,255,0.25)',
              border: 'none',
              borderRadius: 6,
              color: '#fff',
              fontSize: 18,
              width: 32,
              height: 32,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{overflowY: 'auto', flex: 1, padding: 16}}>
          {violations.length > 0 && (
            <div style={{marginBottom: violations.length > 0 && warnings.length > 0 ? 20 : 0}}>
              <div
                style={{
                  background: '#fee2e2',
                  border: '1px solid #fca5a5',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#991b1b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                🚫 Blocking — save prevented ({violations.length})
              </div>
              {violations.map((v, i) => (
                <IssueCard
                  key={i}
                  issue={v}
                  color='red'
                  onClick={() => window.dispatchEvent(new CustomEvent('a11y/highlightIssue', {detail: {type: v.type, context: v.context}}))}
                  suggestion={A11Y_SUGGESTIONS[v.type as WarningType]}
                  onSuggest={canSuggest(v.type) ? () => requestSuggestion(v) : undefined}
                  loading={pendingKey === issueKey(v)}
                />
              ))}
            </div>
          )}

          {warnings.length > 0 && (
            <div>
              <div
                style={{
                  background: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#92400e',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                ⚠️ Warnings — saved but needs review ({warnings.length})
              </div>
              {warnings.map((w, i) => (
                <IssueCard
                  key={i}
                  issue={w}
                  color='yellow'
                  onClick={() => window.dispatchEvent(new CustomEvent('a11y/highlightIssue', {detail: {type: w.type, context: w.context}}))}
                  suggestion={A11Y_SUGGESTIONS[w.type as WarningType]}
                  onSuggest={canSuggest(w.type) ? () => requestSuggestion(w) : undefined}
                  loading={pendingKey === issueKey(w)}
                />
              ))}
            </div>
          )}

          {scanned && (
            <div style={{marginTop: violations.length || warnings.length ? 20 : 0}}>
              <div
                style={{
                  background: '#dbeafe',
                  border: '1px solid #93c5fd',
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#1e40af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                🔍 Scan results ({scanResults.length})
              </div>
              {scanResults.length === 0 ? (
                <div style={{fontSize: 13, color: '#047857', padding: '4px 2px'}}>
                  ✓ No accessibility issues found in this content.
                </div>
              ) : (
                scanResults.map((s, i) => (
                  <IssueCard
                    key={`scan-${i}`}
                    issue={s}
                    color='yellow'
                    onClick={() => window.dispatchEvent(new CustomEvent('a11y/highlightIssue', {detail: {type: s.type, context: s.context}}))}
                  />
                ))
              )}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 16px',
            borderTop: '1px solid #e5e7eb',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {hasAutoFixable && (
            <button
              onClick={handleFixAll}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                borderRadius: 6,
                background: '#059669',
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ✨ Fix All Auto-Fixable Issues
            </button>
          )}
          {violations.length > 0 && (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('a11y/forceSave'))}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #fca5a5',
                borderRadius: 6,
                background: 'transparent',
                color: '#991b1b',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Save Anyway
            </button>
          )}
          <button
            onClick={() => {
              setViolations([])
              setWarnings([])
              setScanResults([])
              setScanned(false)
              setOpen(false)
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: 6,
              background: '#f9fafb',
              color: '#374151',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Dismiss all
          </button>
        </div>
      </div>
    </>
  )
}

export default A11yWarningPanel
